import { useState } from 'react';
import { Link } from 'react-router';
import type { ProductCard, ProductType } from '@product-portal/shared';
import { FilterChips } from '@/components/portal/filter-chips';
import { useProducts, useReleaseNotes } from '@/hooks/use-requests';
import { formatDate, formatDayMonth, shortName } from '@/lib/format';
import { PRODUCT_TYPE_LABELS } from '@/lib/labels';

type TypeFilter = 'all' | Extract<ProductType, 'transverse' | 'project'>;

/**
 * One card per product. Not reproduced for lack of a source (see CLAUDE.md,
 * « Écarts lot 2 »): health state, ticket progress, incidents.
 */
function ProductCardView({ product }: { product: ProductCard }) {
  return (
    <article className="flex flex-col gap-3 rounded-card border border-border bg-card px-5 py-[18px]">
      <h2 className="m-0 text-base font-bold">{product.name}</h2>
      <span className="text-xs text-muted-foreground">
        {PRODUCT_TYPE_LABELS[product.type]}
        {product.owners.length > 0 && ` · PO : ${product.owners.map(shortName).join(', ')}`}
      </span>
      <span className="text-xs text-muted-foreground">
        {product.nextVersion
          ? `Version ${product.nextVersion.number}${product.nextVersion.plannedDate ? ` · ${formatDayMonth(product.nextVersion.plannedDate)}` : ''}`
          : 'Pas de version planifiée'}
      </span>
      <span className="text-xs text-muted-foreground">
        {product.openRequestCount === 1 ? '1 demande ouverte' : `${product.openRequestCount} demandes ouvertes`}
      </span>
      <Link to={`/release-notes?produit=${product.id}`} className="text-[13px] font-bold no-underline">
        Release notes →
      </Link>
    </article>
  );
}

function LatestReleaseNotes({ products }: { products: ProductCard[] }) {
  const [productId, setProductId] = useState<string>('all');
  const notes = useReleaseNotes(productId === 'all' ? undefined : productId);
  return (
    <section aria-labelledby="rn" className="flex flex-col rounded-card border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
        <h2 id="rn" className="m-0 text-base font-bold">
          Release notes
        </h2>
        <FilterChips
          ariaLabel="Produit des release notes"
          value={productId}
          onChange={setProductId}
          options={[{ value: 'all', label: 'Tous les produits' }, ...products.map((p) => ({ value: p.id, label: p.name }))]}
        />
      </div>
      {notes.data && notes.data.length === 0 && <p className="m-0 px-6 py-4 text-sm text-muted-foreground">Aucune release note publiée.</p>}
      <ul className="m-0 flex list-none flex-col p-0">
        {(notes.data ?? []).slice(0, 5).map((note) => (
          <li key={note.id} className="flex flex-wrap items-start gap-5 border-b border-muted px-6 py-4 last:border-b-0">
            <span className="w-[84px] shrink-0 text-xs text-muted-foreground tabular-nums">{formatDate(note.deliveredOn ?? note.publishedAt)}</span>
            <div className="flex min-w-60 flex-1 flex-col gap-1.5">
              <Link to={`/release-notes?note=${note.id}`} className="text-[15px] font-bold text-foreground no-underline hover:text-foreground">
                {note.product.name} {note.versionNumber} — {note.title}
              </Link>
              <p className="m-0 text-[13px] leading-normal text-foreground-secondary">{note.summary}</p>
              {note.closedRequests.length > 0 && (
                <span className="text-xs text-muted-foreground">Répond à {note.closedRequests.map((r) => r.reference).join(', ')}</span>
              )}
            </div>
            <Link to={`/release-notes?note=${note.id}`} className="whitespace-nowrap text-[13px] font-bold no-underline">
              Lire la note complète
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 04 · Produits & projets, read only. */
export function ProductsPage() {
  const products = useProducts();
  const [type, setType] = useState<TypeFilter>('all');
  const all = products.data ?? [];
  const shown = type === 'all' ? all : all.filter((p) => p.type === type);
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="m-0 text-[26px] font-black tracking-[-0.01em]">Produits &amp; projets</h1>
        {/* « Site média » products appear under « Tous » only: the mock-up has no chip for them (see CLAUDE.md). */}
        <FilterChips<TypeFilter>
          ariaLabel="Type de produit"
          value={type}
          onChange={setType}
          options={[
            { value: 'all', label: 'Tous' },
            { value: 'transverse', label: 'Produits transverses' },
            { value: 'project', label: 'Projets' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((product) => (
          <ProductCardView key={product.id} product={product} />
        ))}
        <article className="flex flex-col items-start justify-center gap-2 rounded-card border border-dashed border-input bg-card px-5 py-[18px]">
          <h2 className="m-0 text-[15px] font-bold text-foreground-secondary">Un besoin sur un autre sujet ?</h2>
          <p className="m-0 text-[13px] text-muted-foreground">
            Les demandes hors produit référencé sont qualifiées par l'équipe et rattachées au bon périmètre.
          </p>
          <Link to="/demandes/nouvelle" className="text-[13px] font-bold no-underline">
            Soumettre une demande →
          </Link>
        </article>
      </div>

      <LatestReleaseNotes products={all} />
    </>
  );
}
